<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import { ExclamationCircleIcon, QuestionMarkCircleIcon, CheckIcon } from '@heroicons/vue/20/solid';
import { fr } from 'date-fns/locale';
import BaseButton from '../buttons/BaseButton.vue';

const props = defineProps<{
  label?: string;
  description?: string;
  placeholder?: string;
  default?: string;
  validateFn: (data: string) => boolean;
  icon?: any;
  tooltip?: string;
}>();

const emits = defineEmits<{
  (e: 'update:modelValue', textInput?: string | null): void
  (e: 'submit', textInput?: string | null): void
}>();

const textInput = ref(props.default || "");
const hasError = ref(false);
const showTooltip = ref(false);

const isValid = computed(() => props.validateFn(textInput.value))

const inputClasses = computed(() => {
  if (props.validateFn(textInput.value)) return "block w-full rounded-md border-0 py-1.5 placeholder-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
  return hasError.value 
    ? "block w-full rounded-md border-0 py-1.5 pr-10 text-red-900 ring-1 ring-inset ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500 sm:text-sm sm:leading-6"
    : "block w-full rounded-md border-0 py-1.5 placeholder-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6";
});


watch(textInput, (newValue) => emits("update:modelValue", textInput.value))
</script>
<template>
  <div class="text-left">
    <div>
      <label v-if="label" for="date" class="block text-sm font-medium leading-6 text-gray-900">{{ props.label }} :</label>
      <span v-if="props.description" class="block text-xs italic text-slate-600">({{ props.description }})</span>
    </div>
    <div class="inline-flex items-center justify-center">
      <div class="relative rounded-md shadow-sm">
        <input 
          @focusin="showTooltip = true"
          @focusout="showTooltip = false"
          @keydown="(event: KeyboardEvent) => {
            if (event.key === 'Enter') emits('submit', textInput);
            showTooltip = false;
          }"
          v-model="textInput" 
          type="text"
          :class="[inputClasses, icon ? 'pl-9' : 'pr-9']"
          :aria-invalid="hasError"
          :placeholder="props.placeholder || '(vide)'"
          class="block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
        />
        <div v-if="isValid && !icon" class="absolute inset-y-0 z-10 flex items-center pointer-events-none right-2">
          <CheckIcon class="w-5 h-5 text-green-600" aria-hidden="true" />
        </div>
        <div
          v-else-if="tooltip"
          @mouseover="showTooltip = true"
          v-tooltip.bottom="{
            shown: showTooltip,
            content: tooltip,
            triggers: ['hover', 'focus'],
          }" class="absolute inset-y-0 z-50 flex items-center pointer-events-none right-2">
          <QuestionMarkCircleIcon class="w-5 h-5 text-slate-400" aria-hidden="true" />
        </div>
        <div v-if="hasError" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none right-5">
          <ExclamationCircleIcon class="w-5 h-5 text-red-500" aria-hidden="true" />
        </div>
        <div v-if="icon" class="absolute inset-y-0 flex items-center pr-3 pointer-events-none left-2">
          <component :is="icon" class="w-5 h-5 text-slate-400" aria-hidden="true" />
        </div>
      </div>
      <div v-if="isValid" class="ml-1">
        <BaseButton text="" size="md" :icon="CheckIcon" @click="emits('submit', textInput)" />
      </div>
    </div>

    <p v-if="hasError" class="mt-2 text-sm text-red-600" id="price-error">Merci d'entrer une date au format dd/MM/yyyy HH:mm.</p>
  </div>
</template>
